import * as PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconFilm, IconPlay, IconCheveronUp, IconCheveronDown } from '../Icons';
import Link from '../Link';

const VideoBox = ({ onToggle, summaryVideo, videos, helpCenterUrl, hidden }) => {
  const { t } = useTranslation('components');

  const videoTitles = t('physician_welcome_screen.VideoBox.videos', { returnObjects: true });

  return (
    <div data-component="PhysicianWelcomeScreen__VideoBox" className="mb-8">
      <h2 className="font-semibold text-xl text-slate-500 mb-2 flex-1 flex items-center space-x-2">
        <IconFilm className="mr-2" />
        {t('physician_welcome_screen.VideoBox.title', {
          allowHtml: true,
        })}
      </h2>
      <div className="w-full border pb-4 bg-white">
        {!hidden && (
          <>
            <button
              type="button"
              onClick={summaryVideo.onClick}
              className="flex group focus:outline-none w-full items-center pt-4 px-4 space-y-4 lg:space-y-0 lg:space-x-8"
            >
              <div className="w-1/2 bg-slate-200 lg:w-1/3 flex-shrink-0 shadow group-hover:shadow-lg">
                <div className="w-full pb-2/3 relative overflow-hidden">
                  <img
                    className="w-full h-full absolute object-cover"
                    alt={t('physician_welcome_screen.VideoBox.summary.title')}
                    src={summaryVideo.image}
                  />
                  <div className="absolute inset-1/2 -mt-6 -ml-6">
                    <div className="rounded-full bg-slate-800 w-12 h-12 flex relative justify-center items-center">
                      <IconPlay className="fill-current text-slate-200 -mr-1 w-8 h-8" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col flex-1 text-left ml-2 lg:ml-0">
                <h3 className="text-blue-500 font-semibold text-lg lg:text-xl">
                  {t('physician_welcome_screen.VideoBox.summary.title')}
                </h3>
                <div className="text-lg text-slate-700 mt-2 hidden lg:inline">
                  {t('physician_welcome_screen.VideoBox.summary.text')}
                </div>
                <div className="text-slate-500">
                  {t('physician_welcome_screen.VideoBox.duration', {
                    duration: summaryVideo.duration,
                  })}
                </div>
              </div>
            </button>
            <hr className="border-blue-200 border-t rounded-full flex lg:hidden mt-4 mx-4" />
            <div className="flex mt-4 lg:mt-20 flex-col lg:flex-row px-4">
              {videos.map(({ id, onClick: onClickVideo, image, duration }, i) => (
                <div
                  key={id}
                  className="flex items-center lg:flex-col flex-1 mt-4 lg:mt-0 lg:justify-center"
                >
                  <div
                    className={`hidden lg:flex justify-center lg:w-full lg:bg-slate-100 lg:pr-8 h-3 ${
                      i === 0 ? 'rounded-l-full' : ''
                    } ${i === videos.length - 1 ? 'rounded-r-full' : ''}`}
                  >
                    <div className="rounded-full -mt-4 p-2 bg-slate-400 text-white w-6 h-6 lg:w-10 lg:h-10 flex justify-center items-center">
                      {i + 1}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClickVideo}
                    className="group focus:outline-none lg:ml-0 lg:pr-8 flex lg:flex-col flex-1 lg:w-full items-center"
                  >
                    <div className="w-1/2 bg-slate-200 lg:w-full lg:mt-8 flex-shrink-0 shadow-md group-hover:shadow-lg">
                      <div className="w-full pb-2/3 relative overflow-hidden">
                        <img
                          className="w-full h-full absolute object-cover"
                          alt={videoTitles[id]}
                          src={image}
                        />
                        <div className="absolute inset-1/2 -mt-6 -ml-6 lg:-mt-4 lg:-ml-4">
                          <div className="rounded-full bg-slate-800 w-12 h-12 lg:w-8 lg:h-8 flex relative justify-center items-center">
                            <IconPlay className="fill-current text-slate-200 -mr-1 w-8 h-8 lg:w-6 lg:h-6" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col flex-1 w-full ml-2 lg:ml-0 text-left">
                      <h3 className="text-blue-500 lg:ml-0 text-lg font-semibold mt-4">
                        {videoTitles[id]}
                      </h3>
                      <div className="text-slate-500">
                        {t('physician_welcome_screen.VideoBox.duration', { duration })}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-center lg:justify-end lg:px-8 lg:text-lg mt-8">
              <Link target="_blank" href={helpCenterUrl}>
                {t('physician_welcome_screen.VideoBox.help')}
              </Link>
            </div>
            <div className="flex justify-center lg:justify-start lg:px-8 mt-8">
              <button
                type="button"
                onClick={onToggle}
                className="text-slate-600 hover:text-slate-700 focus:outline-none flex items-center"
              >
                <IconCheveronUp className="fill-current" />
                <div className="focus:outline-none">
                  {t('physician_welcome_screen.VideoBox.hide')}
                </div>
              </button>
            </div>
          </>
        )}
        {hidden && (
          <div className="flex flex-col px-4 pt-4">
            <div className="flex flex-wrap">
              {t('physician_welcome_screen.VideoBox.empty_state', {
                allowHtml: true,
                url: helpCenterUrl,
              })}
            </div>
            <div className="flex justify-center lg:justify-start mt-8">
              <button
                type="button"
                onClick={onToggle}
                className="text-slate-600 hover:text-slate-700 focus:outline-none flex items-center"
              >
                <IconCheveronDown className="fill-current" />
                <div className="focus:outline-none">
                  {t('physician_welcome_screen.VideoBox.show')}
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

VideoBox.defaultProps = {
  onClick: () => {},
  onToggle: () => {},
};

VideoBox.propTypes = {
  onClick: PropTypes.func,
  onToggle: PropTypes.func,
  summaryVideo: PropTypes.shape({
    image: PropTypes.string,
    duration: PropTypes.string,
    onClick: PropTypes.func,
  }).isRequired,
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      id: PropTypes.oneOf(['profile', 'patients', 'colleagues', 'video']),
      duration: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ).isRequired,
  helpCenterUrl: PropTypes.string.isRequired,
  hidden: PropTypes.bool.isRequired,
};

export default VideoBox;
